/**
 * CID Misrouting Detection for Phoenix LiveView
 *
 * TWO MODES:
 *
 * 1. ALWAYS-ON ORPHANED CID DETECTOR (initOrphanedCidDetector):
 *    Detects events targeting a CID that no longer exists in the DOM.
 *    Reports to server via Logger.warning (not AppSignal exceptions).
 *    Client-side throttled: max 1 report per event name per 60s.
 *
 * 2. OPT-IN VERBOSE MODE (initCidDebug, for local debugging):
 *    sessionStorage.setItem("enaia:cid_debug", "true"); location.reload()
 *    Adds: console logging for ALL events, MutationObserver, periodic CID
 *    snapshots, LiveView debug mode, window.__cidDebugLog array.
 *
 *    Disable: sessionStorage.removeItem("enaia:cid_debug"); location.reload()
 *    Shortcut: add ?cid_debug=1 to URL
 */

const PREFIX = "[CID-DEBUG]"
const SESSION_KEY = "enaia:cid_debug"

function enable() {
  try {
    sessionStorage.setItem(SESSION_KEY, "true")
    window.location.reload()
  } catch (_error) {
    console.warn(PREFIX, "Could not enable verbose mode")
  }
}

function disable() {
  try {
    sessionStorage.removeItem(SESSION_KEY)
    window.location.reload()
  } catch (_error) {
    console.warn(PREFIX, "Could not disable verbose mode")
  }
}

function isVerbose() {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get("cid_debug") === "1") {
      sessionStorage.setItem(SESSION_KEY, "true")
    }
    return sessionStorage.getItem(SESSION_KEY) === "true"
  } catch (_error) {
    return false
  }
}

window.enableCidDebug = enable
window.disableCidDebug = disable

// ── DOM path utility ──

function domPath(el) {
  const path = []
  try {
    let node = el
    while (node && node !== document.documentElement) {
      const entry = { tag: node.tagName, id: node.id || null }
      const cid = node.getAttribute && node.getAttribute("data-phx-component")
      if (cid) entry.cid = cid
      path.push(entry)
      node = node.parentElement
    }
  } catch (_e) {
    // detached node
  }
  return path
}

// ── Helpers for orphaned CID detection ──

function isLiteralCid(value) {
  return value != null && /^\d+$/.test(String(value))
}

function cidExistsInDom(cid) {
  return document.querySelector(`[data-phx-component="${cid}"]`) !== null
}

function getViewProto(liveSocket) {
  for (const rootId in liveSocket.roots) {
    const root = liveSocket.roots[rootId]
    if (root?.constructor?.prototype) return root.constructor.prototype
  }
  return null
}

// ── Always-on orphaned CID detector ──

const orphanedThrottle = new Map()
const THROTTLE_MS = 60_000

function isThrottled(key) {
  const last = orphanedThrottle.get(key)
  return last != null && Date.now() - last < THROTTLE_MS
}

function markReported(key) {
  orphanedThrottle.set(key, Date.now())
}

function reportOrphanedCid(view, origPushEvent, data) {
  try {
    const el = document.querySelector("[data-phx-main]") || document.body
    origPushEvent.call(view, "event", el, null, "cid-orphaned-target", data, {})
  } catch (_e) {
    // never break event dispatch
  }
}

export function initOrphanedCidDetector() {
  const liveSocket = window.liveSocket
  if (!liveSocket) return

  const viewProto = getViewProto(liveSocket)
  if (!viewProto || viewProto.__orphanedCidPatched) return
  viewProto.__orphanedCidPatched = true

  // ── pushEvent ──
  const origPushEvent = viewProto.pushEvent
  viewProto.pushEvent = function (
    type,
    el,
    targetCtx,
    phxEvent,
    meta,
    opts,
    onReply,
  ) {
    try {
      if (phxEvent !== "cid-orphaned-target") {
        const phxTarget = el?.getAttribute?.("phx-target")
        if (isLiteralCid(phxTarget)) {
          const throttleKey = `${phxEvent}:${phxTarget}`
          if (!isThrottled(throttleKey) && !cidExistsInDom(phxTarget)) {
            markReported(throttleKey)
            const elCid = el
              ?.closest?.("[data-phx-component]")
              ?.getAttribute("data-phx-component")
            reportOrphanedCid(this, origPushEvent, {
              event: phxEvent,
              target_cid: phxTarget,
              element_cid: elCid,
              element_id: el?.id || null,
              view_id: this.id,
            })
          }
        }
      }
    } catch (_e) {
      // never break event dispatch
    }

    return origPushEvent.call(
      this,
      type,
      el,
      targetCtx,
      phxEvent,
      meta,
      opts,
      onReply,
    )
  }

  // ── pushInput ──
  const origPushInput = viewProto.pushInput
  viewProto.pushInput = function (
    inputEl,
    targetCtx,
    forceCid,
    phxEvent,
    opts,
    callback,
  ) {
    try {
      const form = inputEl.form
      const phxTarget = form?.getAttribute?.("phx-target")
      if (isLiteralCid(phxTarget)) {
        const throttleKey = `${phxEvent}:${phxTarget}`
        if (!isThrottled(throttleKey) && !cidExistsInDom(phxTarget)) {
          markReported(throttleKey)
          const elCid = inputEl
            ?.closest?.("[data-phx-component]")
            ?.getAttribute("data-phx-component")
          reportOrphanedCid(this, origPushEvent, {
            event: phxEvent,
            target_cid: phxTarget,
            element_cid: elCid,
            element_id: inputEl?.id || null,
            form_id: form?.id || null,
            view_id: this.id,
          })
        }
      }
    } catch (_e) {
      // never break event dispatch
    }

    return origPushInput.call(
      this,
      inputEl,
      targetCtx,
      forceCid,
      phxEvent,
      opts,
      callback,
    )
  }

  // ── submitForm ──
  const origSubmitForm = viewProto.submitForm
  if (origSubmitForm) {
    viewProto.submitForm = function (
      formEl,
      targetCtx,
      phxEvent,
      submitter,
      opts,
      callback,
    ) {
      try {
        const phxTarget = formEl?.getAttribute?.("phx-target")
        if (isLiteralCid(phxTarget)) {
          const throttleKey = `${phxEvent}:${phxTarget}`
          if (!isThrottled(throttleKey) && !cidExistsInDom(phxTarget)) {
            markReported(throttleKey)
            const elCid = formEl
              ?.closest?.("[data-phx-component]")
              ?.getAttribute("data-phx-component")
            reportOrphanedCid(this, origPushEvent, {
              event: phxEvent,
              target_cid: phxTarget,
              element_cid: elCid,
              element_id: formEl?.id || null,
              view_id: this.id,
            })
          }
        }
      } catch (_e) {
        // never break event dispatch
      }

      return origSubmitForm.call(
        this,
        formEl,
        targetCtx,
        phxEvent,
        submitter,
        opts,
        callback,
      )
    }
  }
}

// ── Verbose mode initialization (opt-in, for local debugging) ──

export function initCidDebug() {
  const verbose = isVerbose()
  if (!verbose) return

  const liveSocket = window.liveSocket
  if (!liveSocket) {
    console.warn(PREFIX, "window.liveSocket not found")
    return
  }

  const log = []
  window.__cidDebugLog = log
  const record = function (entry) {
    entry.timestamp = new Date().toISOString()
    log.push(entry)
    if (log.length > 5000) log.splice(0, log.length - 5000)
  }

  // ── 1. Monkey-patch View.prototype ──

  try {
    let viewProto = null
    for (const rootId in liveSocket.roots) {
      const root = liveSocket.roots[rootId]
      if (root?.constructor?.prototype) {
        viewProto = root.constructor.prototype
        break
      }
    }

    if (viewProto && !viewProto.__cidDebugPatched) {
      viewProto.__cidDebugPatched = true

      // ── pushEvent ──
      const origPushEvent = viewProto.pushEvent
      viewProto.pushEvent = function (
        type,
        el,
        targetCtx,
        phxEvent,
        meta,
        opts,
        onReply,
      ) {
        try {
          const cid = this.targetComponentID(el, targetCtx, opts || {})
          const elCid = el
            ?.closest?.("[data-phx-component]")
            ?.getAttribute("data-phx-component")

          if (cid != null) {
            const closestCid = this.closestComponentID(targetCtx)
            const phxTarget = el?.getAttribute?.("phx-target")
            const entry = {
              type: "event-push",
              eventType: type,
              eventName: phxEvent,
              resolvedCid: cid,
              closestCid,
              elementCid: elCid,
              phxTarget,
              source: el
                ? {
                    tag: el.tagName,
                    id: el.id || null,
                    name: el.name || null,
                    phxChange: el.getAttribute?.("phx-change"),
                    phxSubmit: el.getAttribute?.("phx-submit"),
                    formId: el.form?.id,
                  }
                : null,
              domPath: el ? domPath(el) : [],
              viewId: this.id,
            }
            record(entry)
            console.log(
              PREFIX,
              `pushEvent "${phxEvent}" (${type}) -> CID ${cid}`,
              {
                resolvedCid: cid,
                closestCid,
                elementCid: elCid,
                phxTarget,
                el,
              },
            )
          }
        } catch (e) {
          console.warn(PREFIX, "error in pushEvent wrapper:", e)
        }

        return origPushEvent.call(
          this,
          type,
          el,
          targetCtx,
          phxEvent,
          meta,
          opts,
          onReply,
        )
      }

      // ── pushInput ──
      const origPushInput = viewProto.pushInput
      viewProto.pushInput = function (
        inputEl,
        targetCtx,
        forceCid,
        phxEvent,
        opts,
        callback,
      ) {
        try {
          const resolvedCid =
            forceCid != null
              ? forceCid
              : this.targetComponentID(inputEl.form, targetCtx, opts || {})
          const elCid = inputEl
            .closest?.("[data-phx-component]")
            ?.getAttribute("data-phx-component")
          const formCid = inputEl.form
            ?.closest?.("[data-phx-component]")
            ?.getAttribute("data-phx-component")

          if (resolvedCid != null) {
            const entry = {
              type: "input-push",
              eventName: phxEvent,
              resolvedCid,
              forceCid,
              elementCid: elCid,
              formCid,
              source: {
                tag: inputEl.tagName,
                id: inputEl.id || null,
                name: inputEl.name || null,
                formId: inputEl.form?.id,
                phxTarget: inputEl.form?.getAttribute?.("phx-target"),
              },
              domPath: domPath(inputEl),
              viewId: this.id,
            }
            record(entry)
            console.log(
              PREFIX,
              `pushInput "${phxEvent}" -> CID ${resolvedCid}`,
              {
                resolvedCid,
                forceCid,
                elementCid: elCid,
                formCid,
                el: inputEl,
              },
            )
          }
        } catch (e) {
          console.warn(PREFIX, "error in pushInput wrapper:", e)
        }

        return origPushInput.call(
          this,
          inputEl,
          targetCtx,
          forceCid,
          phxEvent,
          opts,
          callback,
        )
      }

      // ── submitForm ──
      const origSubmitForm = viewProto.submitForm
      if (origSubmitForm) {
        viewProto.submitForm = function (
          formEl,
          targetCtx,
          phxEvent,
          submitter,
          opts,
          callback,
        ) {
          try {
            const cid = this.targetComponentID(formEl, targetCtx)
            const elCid = formEl
              .closest?.("[data-phx-component]")
              ?.getAttribute("data-phx-component")

            const entry = {
              type: "submit-push",
              eventName: phxEvent,
              resolvedCid: cid,
              elementCid: elCid,
              source: {
                tag: formEl.tagName,
                id: formEl.id || null,
                phxTarget: formEl.getAttribute?.("phx-target"),
              },
              domPath: domPath(formEl),
              viewId: this.id,
            }

            record(entry)
            console.log(PREFIX, `submitForm "${phxEvent}" -> CID ${cid}`, {
              cid,
              elementCid: elCid,
              form: formEl,
            })
          } catch (e) {
            console.warn(PREFIX, "error in submitForm wrapper:", e)
          }

          return origSubmitForm.call(
            this,
            formEl,
            targetCtx,
            phxEvent,
            submitter,
            opts,
            callback,
          )
        }
      }
    }
  } catch (e) {
    console.warn(PREFIX, "failed to patch View prototype:", e)
  }

  // ── 2. Hook into onBeforeElUpdated to detect CID changes during morphdom ──

  try {
    if (liveSocket) {
      const existingDom = liveSocket.domCallbacks
      const origOnBeforeElUpdated = existingDom.onBeforeElUpdated

      existingDom.onBeforeElUpdated = function (fromEl, toEl) {
        try {
          const fromCid =
            fromEl.getAttribute && fromEl.getAttribute("data-phx-component")
          const toCid =
            toEl.getAttribute && toEl.getAttribute("data-phx-component")

          if (fromCid !== null && toCid !== null && fromCid !== toCid) {
            const entry = {
              type: "cid-morph-mismatch",
              fromCid,
              toCid,
              elementId: fromEl.id || null,
              elementTag: fromEl.tagName,
              fromClasses: fromEl.className,
              toClasses: toEl.className,
            }
            record(entry)
            console.warn(
              PREFIX,
              `MORPH CID MISMATCH: element ${fromEl.tagName}#${fromEl.id || "(no-id)"} ` +
                `has CID ${fromCid} but morphdom is updating it to CID ${toCid}`,
              { fromEl, toEl, entry },
            )
          }

          if (
            (fromCid === null && toCid !== null) ||
            (fromCid !== null && toCid === null)
          ) {
            const entry = {
              type: "cid-morph-added-or-removed",
              fromCid,
              toCid,
              elementId: fromEl.id || null,
              elementTag: fromEl.tagName,
            }
            record(entry)
            console.log(
              PREFIX,
              `CID ${fromCid === null ? "added" : "removed"} during morph: ` +
                `${fromEl.tagName}#${fromEl.id || "(no-id)"} ${fromCid} -> ${toCid}`,
              { fromEl, toEl },
            )
          }
        } catch (e) {
          // never break morphdom
        }

        if (origOnBeforeElUpdated) {
          return origOnBeforeElUpdated(fromEl, toEl)
        }
      }

      console.log(PREFIX, "onBeforeElUpdated hook installed")
    }
  } catch (e) {
    console.warn(PREFIX, "failed to hook onBeforeElUpdated:", e)
  }

  // ── 3. MutationObserver for data-phx-component attribute changes ──

  try {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-phx-component"
        ) {
          const el = mutation.target
          const oldVal = mutation.oldValue
          const newVal = el.getAttribute("data-phx-component")

          if (oldVal !== newVal) {
            const entry = {
              type: "cid-change",
              oldCid: oldVal,
              newCid: newVal,
              elementId: el.id || null,
              elementTag: el.tagName,
              elementClasses: el.className,
              domPath: domPath(el),
            }
            record(entry)
            console.warn(
              PREFIX,
              `CID CHANGED: ${el.tagName}#${el.id || "(no-id)"} ` +
                `${oldVal} -> ${newVal}`,
              { el, entry },
            )
          }
        }
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-phx-component"],
      attributeOldValue: true,
      subtree: true,
    })

    window.__cidDebugObserver = observer
    console.log(
      PREFIX,
      "MutationObserver installed for data-phx-component changes",
    )
  } catch (e) {
    console.warn(PREFIX, "failed to install MutationObserver:", e)
  }

  // ── 4. Enable LiveView debug mode ──

  try {
    if (liveSocket) {
      liveSocket.enableDebug()
      console.log(
        PREFIX,
        "LiveView debug mode enabled (detectDuplicateIds active)",
      )
    }
  } catch (e) {
    console.warn(PREFIX, "failed to enable debug mode:", e)
  }

  // ── 5. Periodic CID snapshot for drift detection ──

  try {
    let lastSnapshot = {}

    function takeCidSnapshot() {
      const snapshot = {}
      document.querySelectorAll("[data-phx-component]").forEach((el) => {
        const cid = el.getAttribute("data-phx-component")
        const key =
          el.id ||
          `(anon:${el.tagName}:${domPath(el)
            .map((p) => p.tag)
            .join(">")})`
        snapshot[key] = cid
      })
      return snapshot
    }

    function diffSnapshots(prev, curr) {
      const changes = []
      for (const key in curr) {
        if (prev[key] !== undefined && prev[key] !== curr[key]) {
          changes.push({ element: key, oldCid: prev[key], newCid: curr[key] })
        }
      }
      return changes
    }

    const snapshotInterval = setInterval(() => {
      try {
        const curr = takeCidSnapshot()
        const drift = diffSnapshots(lastSnapshot, curr)
        if (drift.length > 0) {
          const entry = { type: "cid-drift", drift }
          record(entry)
          console.warn(PREFIX, "CID DRIFT detected between snapshots:", drift)
        }
        lastSnapshot = curr
      } catch (_e) {
        // never throw in interval
      }
    }, 2000)

    window.__cidDebugSnapshotInterval = snapshotInterval
    lastSnapshot = takeCidSnapshot()
    console.log(
      PREFIX,
      `Initial CID snapshot: ${Object.keys(lastSnapshot).length} components`,
    )
  } catch (e) {
    console.warn(PREFIX, "failed to set up CID snapshots:", e)
  }

  // ── Teardown helper (verbose mode) ──

  window.__cidDebugTeardown = function () {
    try {
      if (window.__cidDebugObserver) {
        window.__cidDebugObserver.disconnect()
        window.__cidDebugObserver = null
      }
      if (window.__cidDebugSnapshotInterval) {
        clearInterval(window.__cidDebugSnapshotInterval)
        window.__cidDebugSnapshotInterval = null
      }
      console.log(
        PREFIX,
        "Observers and intervals torn down. Reload page to remove prototype patches.",
      )
    } catch (e) {
      console.warn(PREFIX, "error during teardown:", e)
    }
  }

  console.log(
    PREFIX,
    "Verbose mode active. Inspect: window.__cidDebugLog. Teardown: window.__cidDebugTeardown()",
  )
}
